import React from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { PLATFORM_BADGES, TESTIMONIALS } from "@/lib/site";
import { fadeUp, cardReveal } from "@/lib/motion";

/**
 * TrustProof
 * Platform badges (permanent brand references) + testimonial cards.
 * Presented as an editorial "credited" layout rather than a generic
 * logo wall to avoid template feel.
 */
export default function TrustProof() {
  return (
    <section
      data-testid="trust-proof"
      className="relative bg-white text-black"
    >
      <div className="container-lw section-lw">
        {/* Platform strip */}
        <div className="grid gap-6 border-b border-black/10 pb-14 md:grid-cols-[1fr_2fr] md:gap-16">
          <div>
            <div className="eyebrow text-[#4F0DFF]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#4F0DFF]" />
              Published where dealerships sell
            </div>
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-[#4A5359]">
              LotWise Auto composes each listing once and publishes it
              across the platforms that actually drive dealership leads.
              One dealership standard, on every channel.
            </p>
          </div>

          <ul className="grid grid-cols-2 gap-px overflow-hidden rounded-[6px] border border-black/10 bg-black/10 sm:grid-cols-3 lg:grid-cols-5">
            {PLATFORM_BADGES.map((b, i) => (
              <motion.li
                key={b.name}
                data-testid={`platform-badge-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                variants={cardReveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="flex flex-col justify-between bg-white p-5 text-left"
              >
                <div className="font-display text-lg font-semibold tracking-tight">
                  {b.name}
                </div>
                <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-black/40">
                  {b.note}
                </div>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Testimonials */}
        <div className="mt-16 grid gap-12 lg:grid-cols-[1fr_1.6fr] lg:gap-20">
          <div>
            <div className="eyebrow text-[#4F0DFF]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#4F0DFF]" />
              What dealerships are saying
            </div>
            <h2 className="mt-5 h-display text-4xl md:text-5xl lg:text-6xl">
              Field voice, <br />
              <span className="text-gradient-lw">not marketing.</span>
            </h2>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-[#4A5359]">
              Real dealership operators. Names anonymised at their
              request as LotWise operates in a competitive regional
              market. Named case studies available on qualified calls.
            </p>
          </div>

          <ul className="grid gap-5 md:grid-cols-2">
            {TESTIMONIALS.map((t, i) => (
              <motion.li
                key={t.quote}
                data-testid={`testimonial-${i}`}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                custom={i}
                className={`relative flex flex-col justify-between rounded-[6px] border border-black/10 bg-[#F4F5F7] p-6 ${
                  i === 0 ? "md:col-span-2 md:bg-black md:text-white" : ""
                }`}
              >
                <Quote
                  className={`h-6 w-6 ${
                    i === 0
                      ? "text-[#0AB5FF] md:text-[#0AB5FF]"
                      : "text-[#4F0DFF]/60"
                  }`}
                />
                <blockquote className="mt-4 font-display text-lg font-medium leading-snug tracking-tight md:text-xl">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <div className="mt-6 flex items-baseline justify-between border-t border-current/10 pt-4 text-[10px] font-semibold uppercase tracking-[0.22em] opacity-70">
                  <span>{t.role}</span>
                  <span>{t.where}</span>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
